/**
 * Helpers del modo "árbol" de la carga masiva: replicar la estructura de
 * carpetas de origen en el destino antes de subir los archivos.
 *
 * Piezas:
 *  - collectFolderPaths(items): deriva las rutas de carpeta únicas (con todos
 *    sus ancestros) a partir del relPath de los items de la cola.
 *  - readChildFolderEntries(ref, firebase): lista las subcarpetas existentes
 *    de un destino (para fusionar en vez de duplicar).
 *  - ensureFolderTree(...): crea/fusiona las carpetas en orden de profundidad
 *    y devuelve un Map(rutaNormalizada → CollectionReference destino).
 *
 * Notas del schema (verificado en actions/database/documents.js):
 *  - `documents.create` resuelve con `{ data: CollectionReference }` — la
 *    colección donde vivirán los hijos de la carpeta creada.
 *  - Las entradas `folders[]` de una subcarpeta pueden estar repartidas en
 *    VARIOS docs dentro de su subcolección (create genera un doc nuevo por
 *    llamada cuando recibe una CollectionReference); en la raíz del cliente
 *    (folders/{cid}) viven en ese único doc. readChildFolderEntries cubre
 *    ambos casos, igual que loadAllFoldersRecursively.js.
 */

import { documents as documentsActions } from '../../actions/database/documents';
import { getChildCollection } from '../documents/folder_ref';

export const MAX_TREE_DEPTH = 20;

/** Clave de comparación case/acentos-estable para fusionar carpetas. */
export const normalizeKey = (segment) =>
  String(segment || '').trim().normalize('NFC').toLowerCase();

/** Normaliza una ruta completa "A/B/C" a su clave de mapa. */
export const normalizePath = (relPath) =>
  String(relPath || '').split('/').filter(Boolean).map(normalizeKey).join('/');

/**
 * Deriva las rutas de carpeta únicas (incluyendo prefijos ancestros) desde
 * los items de la cola, ordenadas por profundidad ascendente. Conserva el
 * primer casing visto como nombre a crear; deduplica por ruta normalizada.
 * Lanza Error (mensaje en español) si alguna ruta supera MAX_TREE_DEPTH.
 *
 * `extraPaths` son rutas de carpeta conocidas por otra vía: el recorrido del
 * arrastre y el selector de directorio las reportan TODAS, incluidas las que
 * no tienen ningún archivo debajo. Sin ellas las carpetas VACÍAS nunca se
 * crearían, porque de los archivos no se puede derivar una carpeta sin
 * archivos.
 *
 * @param {Array<{relPath?: string}>} items
 * @param {string[]} [extraPaths]  rutas de carpeta explícitas
 * @returns {string[]} rutas display ("Carpeta/Sub1"), ordenadas
 */
export function collectFolderPaths(items, extraPaths = []) {
  const byKey = new Map(); // rutaNormalizada -> ruta display

  const addPath = (relPath) => {
    const segs = String(relPath || '').split('/').filter(Boolean);
    if (!segs.length) return;
    if (segs.length > MAX_TREE_DEPTH) {
      throw new Error(
        `La ruta "${relPath}" supera el máximo de ${MAX_TREE_DEPTH} niveles de carpetas permitido.`
      );
    }
    for (let i = 1; i <= segs.length; i++) {
      const prefix = segs.slice(0, i);
      const key = prefix.map(normalizeKey).join('/');
      if (!byKey.has(key)) byKey.set(key, prefix.join('/'));
    }
  };

  for (const it of items || []) addPath(it?.relPath);
  for (const p of extraPaths || []) addPath(p);
  return Array.from(byKey.values()).sort((a, b) => {
    const da = a.split('/').length;
    const db = b.split('/').length;
    return da - db || a.localeCompare(b, 'es');
  });
}

/**
 * Lista las subcarpetas existentes de un destino como {name, collRef}.
 * Acepta CollectionReference (escanea folders[] de TODOS sus docs) o
 * DocumentReference (folders[] del doc). Entradas con referencia
 * irresoluble se descartan con warning.
 */
export async function readChildFolderEntries(ref, firebase) {
  const entries = [];
  const push = (arr) => {
    for (const e of arr || []) {
      if (e && e.name && e.reference) entries.push(e);
    }
  };
  if (typeof ref.doc === 'function') {
    // CollectionReference: las entradas están repartidas entre sus docs.
    const snap = await ref.get();
    for (const d of snap.docs) push(d.data()?.folders);
  } else {
    // DocumentReference (raíz del cliente).
    const snap = await ref.get();
    push(snap.data()?.folders);
  }
  const out = [];
  for (const e of entries) {
    const collRef = getChildCollection(firebase, e.reference);
    if (collRef) out.push({ name: e.name, collRef });
    else console.warn('bulk_tree: entrada de carpeta con referencia irresoluble:', e.name);
  }
  return out;
}

/**
 * Lista los archivos existentes en una carpeta (CollectionReference) como
 * [{ name, fileDocRef }]. Los docs de la subcolección con campo `reference`
 * son punteros a files/{id}; el nombre original vive en ese doc. fileDocRef
 * es lo que espera `files.delete` para eliminar/reemplazar el archivo.
 */
export async function listExistingFiles(collRef) {
  if (!collRef || typeof collRef.doc !== 'function') return [];
  const snap = await collRef.get();
  const pointers = snap.docs.filter(d => d.data()?.reference && typeof d.data().reference.get === 'function');
  const out = [];
  // Lecturas en tandas para no disparar cientos de gets simultáneos.
  const CHUNK = 20;
  for (let i = 0; i < pointers.length; i += CHUNK) {
    const chunk = pointers.slice(i, i + CHUNK);
    const snaps = await Promise.all(chunk.map(d => d.data().reference.get().catch(() => null)));
    for (const s of snaps) {
      if (s?.exists && s.data()?.name) out.push({ name: s.data().name, fileDocRef: s.ref });
    }
  }
  return out;
}

/**
 * Crea (o fusiona con) las carpetas del árbol en el destino, en orden de
 * profundidad. Idempotente: re-ejecutar encuentra lo ya creado y lo reutiliza.
 *
 * @param {object} opts
 * @param {object} opts.targetFolderRef  Destino raíz (Collection o Document ref)
 * @param {string[]} opts.dirPaths       Salida de collectFolderPaths
 * @param {function} opts.dispatch       dispatch de redux
 * @param {object} opts.firebase         objeto de useFirebase()
 * @param {function} [opts.onProgress]   (level, mensaje) → alimenta el log
 * @returns {Promise<{map: Map<string, object>, created: number, merged: number}>}
 */
export async function ensureFolderTree({ targetFolderRef, dirPaths, dispatch, firebase, onProgress }) {
  const map = new Map();           // rutaNormalizada -> CollectionReference
  const childrenCache = new Map(); // clave padre ('' = raíz) -> [{name, collRef}]
  let created = 0;
  let merged = 0;

  const getChildren = async (parentKey, parentRef) => {
    if (!childrenCache.has(parentKey)) {
      childrenCache.set(parentKey, await readChildFolderEntries(parentRef, firebase));
    }
    return childrenCache.get(parentKey);
  };

  for (const displayPath of dirPaths) {
    const segs = displayPath.split('/');
    const name = segs[segs.length - 1];
    const key = segs.map(normalizeKey).join('/');
    const parentKey = segs.slice(0, -1).map(normalizeKey).join('/');
    const parentRef = parentKey ? map.get(parentKey) : targetFolderRef;
    if (!parentRef) {
      throw new Error(`No se pudo resolver la carpeta padre de "${displayPath}".`);
    }

    const children = await getChildren(parentKey, parentRef);
    const existing = children.find(c => normalizeKey(c.name) === normalizeKey(name));
    if (existing) {
      map.set(key, existing.collRef);
      merged++;
      onProgress?.('info', `Carpeta "${displayPath}" ya existe en el destino — se fusiona.`);
      continue;
    }

    let res;
    try {
      res = await dispatch(documentsActions.create({
        reference: parentRef,
        roles: { create: true }, // la feature solo se ofrece a root; las rules imponen la autorización real
        folder: { name, description: 'Creada por carga masiva', isUpload: true }
      }));
    } catch (e) {
      throw new Error(`No se pudo crear la carpeta "${displayPath}": ${e?.message || 'error desconocido'}.`);
    }
    const collRef = res?.data;
    if (!collRef || typeof collRef.doc !== 'function') {
      throw new Error(`No se pudo obtener la referencia de la carpeta creada "${displayPath}".`);
    }
    map.set(key, collRef);
    children.push({ name, collRef }); // mantener la cache coherente sin re-leer
    created++;
    onProgress?.('success', `Carpeta "${displayPath}" creada.`);
  }

  return { map, created, merged };
}
